<script>
import Layout from '@/views/layouts/main'
import PageHeader from '@/components/page-header'
import AccountsReceivable from '@/controller/AccountsReceivable'
import Insert from './insert.vue'
import SummaryCards from './summary-cards.vue'
import PartialReceiveModal from './partial-receive-modal.vue'
import {
  canRegisterReceivablePartial,
  formatCurrencyBRL,
  getReceivableAmounts,
  isPartialReceivable,
  resolveReceivableStatus,
  toNumber
} from '@/helpers/accountsReceivableStatus'
import moment from 'moment'
import Swal from 'sweetalert2'

export default {
  components: { Layout, PageHeader, Insert, SummaryCards, PartialReceiveModal },
  data() {
    return {
      titleBody: 'Contas a Receber',
      items: [
        { text: 'Financeiro', href: '/financeiro' },
        { text: 'Contas a Receber', active: true }
      ],
      currentUser: null,
      currentEmpresa: null,
      contas: [],
      planoContas: [],
      inserindo: false,
      editando: false,
      selectedConta: null,
      loading: false,
      filtering: false,
      showPartialReceiveModal: false,
      partialReceiveSubmitting: false,
      partialReceiveTarget: null,
      params: {
        vencimento_ini: null,
        vencimento_end: null,
        data_pagamento_ini: null,
        data_pagamento_end: null,
        status: null,
        numero_documento: null,
        descricao: null,
        ano_competencia: null,
        mes_competencia: null
      },
      statusOptions: [
        { value: null, text: 'Todos' },
        { value: 0, text: 'Em aberto' },
        { value: 'parcial', text: 'Parcialmente recebido' },
        { value: 1, text: 'Quitado' }
      ],
      mesesOptions: [
        { value: null, text: 'Todos' },
        { value: 1, text: 'Janeiro' },
        { value: 2, text: 'Fevereiro' },
        { value: 3, text: 'Março' },
        { value: 4, text: 'Abril' },
        { value: 5, text: 'Maio' },
        { value: 6, text: 'Junho' },
        { value: 7, text: 'Julho' },
        { value: 8, text: 'Agosto' },
        { value: 9, text: 'Setembro' },
        { value: 10, text: 'Outubro' },
        { value: 11, text: 'Novembro' },
        { value: 12, text: 'Dezembro' }
      ],
      anosOptions: []
    }
  },
  created() {
    this.currentUser = JSON.parse(localStorage.getItem('user'))
    this.currentEmpresa = JSON.parse(localStorage.getItem('currentEmpresa'))
    this.controller = new AccountsReceivable(this.currentUser, this.currentEmpresa)
    
    // Carregar parâmetros salvos do localStorage
    if (localStorage.getItem('contas_receber_params')) {
      this.params = JSON.parse(localStorage.getItem('contas_receber_params'))
    }
    
    // Gerar opções de anos (ano atual e últimos 4 anos)
    const currentYear = new Date().getFullYear()
    this.anosOptions = [{ value: null, text: 'Todos' }]
    for (let i = 0; i < 5; i++) {
      this.anosOptions.push({
        value: currentYear - i,
        text: (currentYear - i).toString()
      })
    }
  },
  mounted() {
    this.load()
    
    // Inicializar filtro com mês atual apenas se não houver filtros salvos
    if (!localStorage.getItem('contas_receber_params')) {
      const now = moment()
      this.params.vencimento_ini = now.startOf('month').format('YYYY-MM-DD')
      this.params.vencimento_end = now.endOf('month').format('YYYY-MM-DD')
      
      // Aplicar filtros automaticamente
      this.onFilters(this.params)
    } else {
      // Aplicar filtros salvos se existirem
      if (this.params && Object.keys(this.params).some(key => this.params[key] !== null)) {
        this.onFilters(this.params)
      }
    }
  },
  computed: {
    contasVisiveis() {
      return this.filterLocalContas(this.contas, this.params)
    },
    contasOrdenadas() {
      return this.contasVisiveis.slice().sort((a, b) => (b?.id || 0) - (a?.id || 0))
    },
    temContaSelecionada() {
      return !!this.selectedConta
    },
    canRegisterPartialReceive() {
      return canRegisterReceivablePartial(this.currentUser)
    },
    isHide() {
      return !this.inserindo && !this.editando
    }
  },
  methods: {
    async load(params = null) {
      try {
        this.loading = true
        const apiParams = this.normalizeApiParams(params || this.params)
        const data = await this.controller.getAccountsReceivable(this.currentEmpresa.id, apiParams)
        this.contas = data.contasReceber || []
        this.planoContas = data.planoContas || []
        this.syncSelectedConta()
      } finally {
        this.loading = false
      }
    },
    insert() {
      this.inserindo = true
    },
    back() {
      this.inserindo = false
      this.editando = false
    },
    async doPost(payload) {
      payload.user_id = this.currentUser?.id
      await this.controller.createAccountReceivable(payload, this.currentEmpresa.id)
      await this.load(this.params)
      this.back()
    },
    selectConta(conta) {
      this.selectedConta = conta
    },
    receivableAmounts(conta) {
      return getReceivableAmounts(conta || {})
    },
    formatCurrency(value) {
      if (value === null || value === undefined || value === '') return '-'
      return formatCurrencyBRL(value)
    },
    formatDate(value) {
      if (!value) return '-'
      return moment(value).format('DD/MM/YYYY')
    },
    formatDateTime(value) {
      if (!value) return '-'
      return moment(value).format('DD/MM/YYYY HH:mm')
    },
    formatCompetencia(conta) {
      // Formatar a competência como "Janeiro de 2025" em portugês BR (moment.js)
      if (!conta?.ano_competencia || !conta?.mes_competencia) return '-'
      const mesIndex = parseInt(conta.mes_competencia, 10) - 1
      const data = moment().year(conta.ano_competencia).month(mesIndex)
      return data.isValid() ? data.locale('pt-br').format('MMMM [de] YYYY') : '-'
    },
    formatStatus(conta) {
      return resolveReceivableStatus(conta).label
    },
    statusVariant(conta) {
      return resolveReceivableStatus(conta).variant
    },
    statusTextClass(conta) {
      return resolveReceivableStatus(conta).textClass
    },
    isContaParcial(conta) {
      return isPartialReceivable(conta)
    },
    canRegisterBaixa(conta) {
      return this.canRegisterPartialReceive && conta?.status !== 1 && this.isContaElegivelBaixa(conta)
    },
    isContaElegivelBaixa(conta) {
      return conta?.elegivel_baixa !== false
    },
    openPartialReceiveModal(conta) {
      if (!this.canRegisterBaixa(conta)) {
        return
      }
      this.partialReceiveTarget = conta
      this.showPartialReceiveModal = true
    },
    closePartialReceiveModal() {
      this.showPartialReceiveModal = false
      this.partialReceiveTarget = null
    },
    normalizeApiParams(params = {}) {
      const apiParams = { ...params }
      if (apiParams.status === 'parcial') {
        delete apiParams.status
      }
      return apiParams
    },
    filterLocalContas(contas = [], params = this.params) {
      if (!Array.isArray(contas) || contas.length === 0) {
        return []
      }

      if (!params || params.status === null || params.status === undefined || params.status === '') {
        return contas
      }

      if (params.status === 'parcial') {
        return contas.filter(conta => this.isContaParcial(conta))
      }

      return contas.filter(conta => toNumber(conta.status) === toNumber(params.status))
    },
    syncSelectedConta() {
      const contasVisiveis = this.filterLocalContas(this.contas, this.params)

      if (!this.selectedConta && contasVisiveis.length) {
        this.selectedConta = contasVisiveis[0]
        return
      }

      if (this.selectedConta) {
        const atualizada =
          contasVisiveis.find(conta => conta.id === this.selectedConta.id) ||
          this.contas.find(conta => conta.id === this.selectedConta.id)
        this.selectedConta = atualizada || contasVisiveis[0] || null
      }
    },
    formatPartialReceiveError(error) {
      const data = error?.response?.data || {}
      const code = data.code || data.error || data.message

      if (code === 'valor_excede_saldo') {
        return `O valor informado (${this.formatCurrency(data.valor ?? data.valor_informado ?? 0)}) é maior que o saldo restante (${this.formatCurrency(data.saldo_restante ?? data.saldo ?? 0)}).`
      }

      if (code === 'titulo_quitado') {
        return 'Este título já está quitado e não aceita novas baixas.'
      }

      if (code === 'titulo_inelegivel') {
        return 'Este título não está disponível para baixa no seu plano.'
      }

      if (code === 'valor_invalido') {
        return 'Informe um valor maior que zero.'
      }

      return data.message || 'Não foi possível registrar a baixa. Tente novamente.'
    },
    extractPartialReceiveData(response) {
      return response?.data ?? response?.conta ?? response ?? {}
    },
    async confirmPartialReceive(payload) {
      this.partialReceiveSubmitting = true
      const saldoAnterior = this.receivableAmounts(this.partialReceiveTarget).saldo
      Swal.fire({
        title: 'Processando...',
        text: 'Registrando baixa parcial',
        allowOutsideClick: false,
        didOpen: () => {
          Swal.showLoading()
        }
      })

      try {
        const response = await this.controller.partialReceiveAccount(
          { ...payload, empresa_id: this.currentEmpresa.id },
          this.currentEmpresa.id
        )
        const contaAtualizada = this.extractPartialReceiveData(response)
        Swal.close()
        this.closePartialReceiveModal()
        await this.load(this.params)
        const saldoRestante = toNumber(
          contaAtualizada.saldo_restante ?? contaAtualizada.saldo ?? Math.max(saldoAnterior - payload.valor_recebido, 0)
        )
        const valorTexto = this.formatCurrency(payload.valor_recebido)
        if (saldoRestante === 0) {
          this.showSuccess('Título quitado com sucesso.')
        } else {
          this.showSuccess(`Baixa de ${valorTexto} registrada. Saldo restante: ${this.formatCurrency(saldoRestante)}.`)
        }
      } catch (error) {
        Swal.close()
        const mensagem = this.formatPartialReceiveError(error)
        if (error?.response?.data?.code === 'titulo_quitado') {
          this.closePartialReceiveModal()
        }
        this.showError(mensagem)
      } finally {
        this.partialReceiveSubmitting = false
      }
    },
    descricaoConta(conta) {
      if (!conta) return 'Sem descrição'
      // Se não tiver descrição, usar o número do documento
      // limita até 40 caracteres, se tiver mais que 40 caracteres, corta e adiciona ...
      if (conta.descricao) {
        return conta.descricao.substring(0, 40) + '...'
      }
      return `# Sem descrição`
    },
    planoDescricao(plano) {
      if (!plano) return '-'
      return plano.descricao || '-'
    },
    tipoPagamentoDescricao(conta) {
      return conta?.tipo_pagamento?.descricao || '-'
    },
    quantidadeParcelas(conta) {
      const valor = conta?.quantidade_parcelas
      return valor ? `${valor} parcela${valor > 1 ? 's' : ''}` : '-'
    },
    temArquivos(conta) {
      return Array.isArray(conta?.arquivos) && conta.arquivos.length > 0
    },
    async onFilters(params) {
      try {
        this.filtering = true
        this.loading = true
        
        // Salvar parâmetros no localStorage
        localStorage.setItem('contas_receber_params', JSON.stringify(params))
        
        // Chamar método do controller com filtros
        const data = await this.controller.getAccountsReceivable(
          this.currentEmpresa.id,
          this.normalizeApiParams(params)
        )
        
        this.contas = data.contasReceber || []
        this.planoContas = data.planoContas || []
        this.syncSelectedConta()
        
        this.showSuccess('Filtros aplicados com sucesso!')
      } catch (error) {
        this.showWarning(error.response?.data?.message || 'Erro ao aplicar filtros')
      } finally {
        this.filtering = false
        this.loading = false
      }
    },
    limparFiltros() {
      this.params = {
        vencimento_ini: null,
        vencimento_end: null,
        data_pagamento_ini: null,
        data_pagamento_end: null,
        status: null,
        numero_documento: null,
        descricao: null,
        ano_competencia: null,
        mes_competencia: null
      }
      localStorage.removeItem('contas_receber_params')
      this.load()
    },
    showSuccess(message) {
      this.$bvToast.toast(message, {
        title: 'Sucesso',
        variant: 'success',
        solid: true,
        toaster: 'b-toaster-bottom-right',
        appendToast: true
      })
    },
    showWarning(message) {
      this.$bvToast.toast(message, {
        title: 'Atenção',
        variant: 'warning',
        solid: true,
        toaster: 'b-toaster-bottom-right',
        appendToast: true
      })
    },
    showError(message) {
      this.$bvToast.toast(message, {
        title: 'Erro',
        variant: 'danger',
        solid: true,
        toaster: 'b-toaster-bottom-right',
        appendToast: true
      })
    },
    async doDelete(contaReceber) {
      // Verifica se a conta está recebida
      if (contaReceber.status === 1) {
        await Swal.fire({
          title: 'Atenção!',
          text: 'Não é possível excluir uma conta que já foi recebida.',
          icon: 'warning',
          confirmButtonColor: '#3085d6',
          confirmButtonText: 'Entendi',
          showCancelButton: false
        });
        return;
      }

      // Perguntar se tem certeza com SweetAlert2
      const result = await Swal.fire({
        title: 'Tem certeza?',
        text: 'Você não poderá reverter isso!',
        icon: 'warning',
        showCancelButton: true,
        confirmButtonColor: '#3085d6',
        cancelButtonColor: '#d33',
        confirmButtonText: 'Sim, excluir!',
        cancelButtonText: 'Cancelar'
      });

      if (!result.isConfirmed) {
        return;
      }

      Swal.fire({
        title: 'Aguarde...',
        text: 'Excluindo conta',
        allowOutsideClick: false,
        didOpen: () => {
          Swal.showLoading();
        }
      });

      try {
        const result = await this.controller.deleteAccountReceivable(contaReceber, this.currentEmpresa.id);
        Swal.close();

        if (result.success) {
          // Manter filtros aplicados após exclusão
          const hasFilters = this.params && Object.keys(this.params).some(key => this.params[key] !== null);
          await this.load(hasFilters ? this.params : null);
          // Se a conta excluída era a selecionada, limpar seleção
          if (this.selectedConta && this.selectedConta.id === contaReceber.id) {
            this.selectedConta = this.contas.length > 0 ? this.contas[0] : null;
          }
          this.showSuccess('Conta excluída com sucesso!');
        } else {
          this.showWarning(result.message || 'Erro ao excluir conta');
        }
      } catch (error) {
        console.error(error);
        Swal.close();
        this.showWarning('Erro ao excluir conta');
      }
    }
  }
}
</script>

<template>
  <Layout>
    <PageHeader :title="titleBody" :items="items" />
    <div class="row">
      <div class="col-md-12">
        <div class="card mb-3">
          <div class="card-body">
            <div class="row align-items-center">
              <div class="col-sm-12 col-md-6">
                <h5 class="mb-0">Resumo de Contas</h5>
                <small class="text-muted">Selecione uma conta para visualizar os detalhes.</small>
              </div>
              <div class="col-sm-12 col-md-6 text-md-right mt-3 mt-md-0">
                <!-- Botão de Filtros -->
                <b-button
                  v-if="isHide"
                  v-b-toggle="'collapse-filters'"
                  variant="primary"
                  class="mb-3 mr-2"
                >
                  <i class="fas fa-filter"></i> Filtros
                </b-button>
                
                <!-- Botão Incluir Recebimento -->
                <b-button v-if="!inserindo && !editando" variant="success" @click="insert" class="mb-3">+ Incluir Recebimento</b-button>
                
                <!-- Botão Voltar -->
                <b-button v-if="inserindo || editando" variant="secondary" @click="back" class="mb-3">Voltar</b-button>
                
                <!-- Botão Atualizar -->
                <b-button variant="outline-primary" class="ml-md-2 mt-2 mt-md-0 mb-3" @click="onFilters(params)" :disabled="filtering || loading">
                  <span v-if="loading || filtering"><b-spinner small class="mr-2" /> Atualizando...</span>
                  <span v-else><i class="bx bx-refresh"></i> Atualizar</span>
                </b-button>
              </div>
            </div>
          </div>
        </div>

        <!-- Collapse de Filtros -->
        <b-collapse id="collapse-filters" class="mt-1" v-if="isHide">
          <b-card>
            <div class="row">
              <!-- Filtro por Vencimento -->
              <div class="col-md-3 mb-3">
                <b-form-group label="Vencimento - Data Inicial:">
                  <input 
                    type="date" 
                    class="form-control" 
                    v-model="params.vencimento_ini"
                  />
                </b-form-group>
              </div>
              <div class="col-md-3 mb-3">
                <b-form-group label="Vencimento - Data Final:">
                  <input 
                    type="date" 
                    class="form-control" 
                    v-model="params.vencimento_end"
                  />
                </b-form-group>
              </div>
              <!-- Filtro por Data de Pagamento -->
              <div class="col-md-3 mb-3">
                <b-form-group label="Data de Pagamento - Inicial:">
                  <input 
                    type="date" 
                    class="form-control" 
                    v-model="params.data_pagamento_ini"
                  />
                </b-form-group>
              </div>
              <div class="col-md-3 mb-3">
                <b-form-group label="Data de Pagamento - Final:">
                  <input 
                    type="date" 
                    class="form-control" 
                    v-model="params.data_pagamento_end"
                  />
                </b-form-group>
              </div>
            </div>

            <div class="row">
              <!-- Filtro por Situação -->
              <div class="col-md-3 mb-3">
                <b-form-group label="Situação:">
                  <b-form-select
                    v-model="params.status"
                    :options="statusOptions"
                  />
                </b-form-group>
              </div>
              
              <!-- Filtro por Documento -->
              <div class="col-md-3 mb-3">
                <b-form-group label="Número do Documento:">
                  <input 
                    type="text" 
                    class="form-control" 
                    v-model="params.numero_documento"
                    placeholder="Digite o número do documento"
                  />
                </b-form-group>
              </div>
              
              <!-- Filtro por Competência - Ano -->
              <div class="col-md-3 mb-3">
                <b-form-group label="Ano de Competência:">
                  <b-form-select
                    v-model="params.ano_competencia"
                    :options="anosOptions"
                  />
                </b-form-group>
              </div>
              <!-- Filtro por Competência - Mês -->
              <div class="col-md-3 mb-3">
                <b-form-group label="Mês de Competência:">
                  <b-form-select
                    v-model="params.mes_competencia"
                    :options="mesesOptions"
                  />
                </b-form-group>
              </div>
            </div>

            <div class="row">
              <!-- Filtro por Descrição -->
              <div class="col-md-12 mb-6">
                <b-form-group label="Descrição:">
                  <input 
                    type="text" 
                    class="form-control" 
                    v-model="params.descricao"
                    placeholder="Digite a descrição"
                  />
                </b-form-group>
              </div>
            </div>

            <!-- Botões de Ação -->
            <div class="row">
              <div class="col-12">
                <b-button 
                  variant="primary" 
                  @click="onFilters(params)"
                  :disabled="filtering || loading"
                  class="mr-2"
                >
                  <i class="fas fa-database"></i> Aplicar Filtros
                </b-button>
                <b-button 
                  variant="secondary" 
                  @click="limparFiltros"
                  :disabled="filtering || loading"
                >
                  <i class="fas fa-eraser"></i> Limpar Filtros
                </b-button>
              </div>
            </div>
          </b-card>
        </b-collapse>

        <!-- Cards de Resumo Financeiro -->
        <div class="row" v-if="isHide && contas.length > 0">
          <div class="col-md-12">
            <SummaryCards :contas="contas" :currentUser="currentUser" />
          </div>
        </div>

        <div v-if="inserindo || editando" class="card">
          <div class="card-body">
            <h4 v-if="inserindo" class="card-title">Incluir Recebimento</h4>
            <Insert
              v-if="inserindo"
              :planoContas="planoContas"
              @doPost="doPost"
              @back="back"
            />
          </div>
        </div>

        <div v-else class="row">
          <div class="col-lg-4 mb-3">
            <div class="card h-100">
              <div class="card-header bg-light">
                <h6 class="mb-0">Contas cadastradas</h6>
              </div>
              <div class="card-body p-0">
                <div v-if="!contasOrdenadas.length" class="p-4 text-center text-muted">
                  Nenhuma conta cadastrada até o momento.
                </div>
                <b-list-group flush v-else class="contas-lista">
                  <b-list-group-item
                    v-for="conta in contasOrdenadas"
                    :key="conta.id"
                    @click="selectConta(conta)"
                    :class="['conta-item', { active: selectedConta && selectedConta.id === conta.id }]"
                  >
                    <div class="d-flex justify-content-between align-items-center">
                      <div class="font-weight-semibold text-truncate mr-2 text-primary">{{ conta.numero_documento }}</div>
                      <b-badge :variant="statusVariant(conta)" :class="statusTextClass(conta)">{{ formatStatus(conta) }}</b-badge>
                    </div>
                    <div class="small text-muted mt-1">
                      Descrição: <strong>{{ descricaoConta(conta) }}</strong>
                    </div>
                    <div class="small text-muted mt-1">
                      Vencimento: <span class="text-danger"><strong>{{ conta.vencimento ? formatDate(conta.vencimento) : '-' }}</strong></span>
                    </div>
                    <div class="small text-muted">
                      Valor: <span class="text-success"><strong>{{ formatCurrency(conta.valor_receber) }}</strong></span>
                    </div>
                  </b-list-group-item>
                </b-list-group>
              </div>
            </div>
          </div>

          <div class="col-lg-8 mb-3">
            <div v-if="temContaSelecionada" class="card h-100">
              <div class="card-body">
                <div class="d-flex justify-content-between align-items-center mb-3">
                  <div>
                    <h4 class="mb-1">{{ selectedConta.numero_documento }}</h4>
                    <div class="text-muted">Código: {{ selectedConta.id }}</div>
                  </div>
                  <div class="text-right">
                    <div>
                      <b-badge
                        :variant="statusVariant(selectedConta)"
                        :class="statusTextClass(selectedConta)"
                      >
                        {{ formatStatus(selectedConta) }}
                      </b-badge>
                    </div>
                    <small class="text-muted d-block mt-1">Atualizado em {{ formatDateTime(selectedConta.updated_at) }}</small>
                  </div>
                </div>

                <div role="tablist">
                  <b-card no-body class="mb-1 shadow-none">
                    <b-card-header header-tag="header" role="tab">
                      <h6 class="m-0">
                        <a
                          v-b-toggle.conta-accordion-1
                          class="text-dark"
                          href="javascript: void(0);"
                        >Informações Gerais</a>
                      </h6>
                    </b-card-header>
                    <b-collapse id="conta-accordion-1" visible accordion="conta-accordion" role="tabpanel">
                      <b-card-body>
                        <div class="row">
                          <div class="col-md-6 col-sm-12 border-detail m-2 p-2">
                            Descrição: <strong>{{ selectedConta.descricao }}</strong>
                          </div>
                          <div class="col-md-5 col-sm-12 border-detail m-2 p-2">
                            Valor a receber: <strong>{{ formatCurrency(selectedConta.valor_receber) }}</strong>
                          </div>
                        </div>
                        <div class="row">
                          <div class="col-md-5 col-sm-12 border-detail m-2 p-2">
                            Situação:
                            <b-badge
                              :variant="statusVariant(selectedConta)"
                              :class="statusTextClass(selectedConta)"
                            >
                              {{ formatStatus(selectedConta) }}
                            </b-badge>
                          </div>
                          <div class="col-md-5 col-sm-12 border-detail m-2 p-2">
                            Já recebido: <strong>{{ formatCurrency(receivableAmounts(selectedConta).received) }}</strong>
                          </div>
                        </div>
                        <div class="row">
                          <div class="col-md-5 col-sm-12 border-detail m-2 p-2">
                            Saldo restante:
                            <strong :class="receivableAmounts(selectedConta).saldo > 0 ? 'text-danger' : 'text-success'">
                              {{ formatCurrency(receivableAmounts(selectedConta).saldo) }}
                            </strong>
                          </div>
                          <div class="col-md-5 col-sm-12 border-detail m-2 p-2">
                            Usuário: <strong>{{ selectedConta?.user?.name || '-' }}</strong>
                          </div>
                        </div>
                      </b-card-body>
                    </b-collapse>
                  </b-card>

                  <b-card no-body class="mb-1 shadow-none">
                    <b-card-header header-tag="header" role="tab">
                      <h6 class="m-0">
                        <a
                          v-b-toggle.conta-accordion-2
                          class="text-dark"
                          href="javascript: void(0);"
                        >Plano de Contas</a>
                      </h6>
                    </b-card-header>
                    <b-collapse id="conta-accordion-2" accordion="conta-accordion" role="tabpanel">
                      <b-card-body>
                        <div class="row">
                          <div class="col-md-6 col-sm-12 border-detail m-2 p-2">
                            Categoria (Débito): <strong>{{ planoDescricao(selectedConta?.conta_debito) }}</strong>
                          </div>
                          <div class="col-md-5 col-sm-12 border-detail m-2 p-2">
                            Conta de Recebimento (Crédito): <strong>{{ planoDescricao(selectedConta?.conta_credito) }}</strong>
                          </div>
                        </div>
                        <div class="row">
                          <div class="col-md-5 col-sm-12 border-detail m-2 p-2">
                            Tipo de Pagamento: <strong>{{ tipoPagamentoDescricao(selectedConta) }}</strong>
                          </div>
                          <div class="col-md-6 col-sm-12 border-detail m-2 p-2">
                            Caixa: <strong>{{ selectedConta?.caixa?.descricao || '-' }}</strong>
                          </div>
                        </div>
                      </b-card-body>
                    </b-collapse>
                  </b-card>

                  <b-card no-body class="mb-1 shadow-none">
                    <b-card-header header-tag="header" role="tab">
                      <h6 class="m-0">
                        <a
                          v-b-toggle.conta-accordion-3
                          class="text-dark"
                          href="javascript: void(0);"
                        >Datas</a>
                      </h6>
                    </b-card-header>
                    <b-collapse id="conta-accordion-3" accordion="conta-accordion" role="tabpanel">
                      <b-card-body>
                        <div class="row">
                          <div class="col-md-4 col-sm-12 border-detail m-2 p-2">
                            Emissão: <strong>{{ formatDate(selectedConta.data_emissao) }}</strong>
                          </div>
                          <div class="col-md-4 col-sm-12 border-detail m-2 p-2">
                            Vencimento: <strong>{{ formatDate(selectedConta.vencimento) }}</strong>
                          </div>
                          <div class="col-md-3 col-sm-12 border-detail m-2 p-2">
                            Recebimento: <strong>{{ formatDate(selectedConta.data_recebimento) }}</strong>
                          </div>
                        </div>
                        <div class="row">
                          <div class="col-md-5 col-sm-12 m-2 p-2">
                            Competência: <strong class="text-capitalize">{{ formatCompetencia(selectedConta) }}</strong>
                          </div>
                        </div>
                      </b-card-body>
                    </b-collapse>
                  </b-card>

                  <b-card no-body class="mb-1 shadow-none">
                    <b-card-header header-tag="header" role="tab">
                      <h6 class="m-0">
                        <a
                          v-b-toggle.conta-accordion-4
                          class="text-dark"
                          href="javascript: void(0);"
                        >Observações</a>
                      </h6>
                    </b-card-header>
                    <b-collapse id="conta-accordion-4" accordion="conta-accordion" role="tabpanel">
                      <b-card-body>
                        <p class="mb-0" v-if="selectedConta?.observacao">{{ selectedConta.observacao }}</p>
                        <p class="text-muted" v-else>Nenhuma observação registrada.</p>
                      </b-card-body>
                    </b-collapse>
                  </b-card>

                  <b-card no-body class="mb-1 shadow-none">
                    <b-card-header header-tag="header" role="tab">
                      <h6 class="m-0">
                        <a
                          v-b-toggle.conta-accordion-baixas
                          class="text-dark"
                          href="javascript: void(0);"
                        >Histórico de baixas</a>
                      </h6>
                    </b-card-header>
                    <b-collapse id="conta-accordion-baixas" accordion="conta-accordion" role="tabpanel">
                      <b-card-body>
                        <div v-if="selectedConta?.baixas && selectedConta.baixas.length" class="table-responsive">
                          <table class="table table-sm mb-0">
                            <thead>
                              <tr>
                                <th>Data</th>
                                <th>Valor</th>
                                <th>Responsável</th>
                                <th>Observação</th>
                              </tr>
                            </thead>
                            <tbody>
                              <tr
                                v-for="baixa in (selectedConta.baixas || []).slice().sort((a, b) => new Date(b.data_recebimento || b.data || b.created_at || 0) - new Date(a.data_recebimento || a.data || a.created_at || 0))"
                                :key="baixa.id"
                              >
                                <td>{{ formatDateTime(baixa.data_recebimento || baixa.data || baixa.created_at) }}</td>
                                <td>{{ formatCurrency(baixa.valor_recebido ?? baixa.valor) }}</td>
                                <td>{{ baixa?.user?.name || '-' }}</td>
                                <td>{{ baixa.observacao || '-' }}</td>
                              </tr>
                            </tbody>
                          </table>
                        </div>
                        <div v-else class="text-muted d-flex align-items-center">
                          <i class="ri-history-line font-size-24 mr-2"></i>
                          Nenhuma baixa registrada para este título.
                        </div>
                      </b-card-body>
                    </b-collapse>
                  </b-card>

                  <b-card no-body class="mb-1 shadow-none" v-if="temArquivos(selectedConta)">
                    <b-card-header header-tag="header" role="tab">
                      <h6 class="m-0">
                        <a
                          v-b-toggle.conta-accordion-5
                          class="text-dark"
                          href="javascript: void(0);"
                        >Arquivos</a>
                      </h6>
                    </b-card-header>
                    <b-collapse id="conta-accordion-5" accordion="conta-accordion" role="tabpanel">
                      <b-card-body>
                        <ul class="list-unstyled mb-0">
                          <li v-for="arquivo in selectedConta.arquivos" :key="arquivo.id" class="mb-2">
                            <a :href="arquivo.url" target="_blank" rel="noopener">{{ arquivo.nome }}</a>
                          </li>
                        </ul>
                      </b-card-body>
                    </b-collapse>
                  </b-card>
                </div>

                <div class="mt-4">
                  <p>Criado: <b-badge variant="secondary">{{ formatDateTime(selectedConta.created_at) }}</b-badge></p>
                  <p>Última alteração: <b-badge variant="secondary">{{ formatDateTime(selectedConta.updated_at) }}</b-badge></p>
                </div>

                <div class="mt-4 d-flex justify-content-end">
                  <b-button
                    variant="success"
                    v-if="canRegisterBaixa(selectedConta)"
                    @click="openPartialReceiveModal(selectedConta)"
                    class="mr-2"
                  >
                    Registrar baixa
                  </b-button>
                  
                  <b-button
                    variant="danger"
                    v-if="selectedConta.status === 0 && receivableAmounts(selectedConta).received === 0"
                    @click="doDelete(selectedConta)"
                  >
                    <i class="bx bx-trash"></i> Excluir
                  </b-button>
                </div>
              </div>
            </div>
            <div v-else class="card h-100">
              <div class="card-body d-flex align-items-center justify-content-center text-muted">
                Selecione uma conta para visualizar os detalhes.
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <PartialReceiveModal
      v-model="showPartialReceiveModal"
      :conta="partialReceiveTarget"
      :planoContas="planoContas"
      :busy="partialReceiveSubmitting"
      @confirm="confirmPartialReceive"
      @close="closePartialReceiveModal"
    />
  </Layout>
</template>

<style scoped>
.border-detail {
  border-right: 1px solid #d5d6d7 !important;
}
.contas-lista {
  max-height: 520px;
  overflow-y: auto;
}
.conta-item {
  cursor: pointer;
  transition: background-color 0.2s ease-in-out;
}
.conta-item:hover {
  background-color: #f5f6f8;
}
.conta-item.active {
  background-color: #e6f4ff;
  border-left: 4px solid #556ee6;
}
</style>
